import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { showModalPopup, showPlaybackError } from "../../../App/state/actions";
import storageConstants from "../../../shared/constants/storage";
import PlayerConstants from "../../../shared/constants/player";
import constants from "../../../shared/constants";
import errorConstants from "../../../shared/constants/error";
import { getAVSPlaybackError } from "../../../shared/utils/playbackHelpers";
import telusConvivaAnalytics from "../../../shared/utils/convivaAnalytics";
import middleware from "../../../shared/middleware";
import { setLocalStorage, removeLocalStorage } from "../../../shared/utils/localStorage";
import {
  handleEntitlementError,
  handleFetchStreamResponse,
  handleFetchStreamError,
} from "../helpers/liveStreamHelpers";

const { VIDEO_AUTH_TOKEN } = storageConstants;
const { ASSET_TYPES } = PlayerConstants;
const { MODAL_TYPES, PAGE_CONTENT_ITEM_TYPES } = constants;
const { AVS_ERROR_CODES } = errorConstants;
const { fetchVideoUrl, fetchVideoToken } = middleware;

export const useLiveChannelTune = ({
  isLivePlayer,
  useRoutePlaybackState,
  playbackAuthToken,
  appProvider,
  currentChannel,
  isPPV,
  exitLookbackMode,
  setAssetId,
  restartLiveStream,
  isCTTEnabled,
  isLookbackStream,
  setVideoUrl,
  setLivePlaybackCanStart,
  contentType,
  cancelTokenSource,
  setAuthToken,
  assetId,
  currentChannelId,
  pcPin,
  pcPinTimer,
  restartPcPinTimer,
  getMediaNodeMetaData,
  isConvivaAppTrackerEnabled,
  userProfile,
  isInHome,
  history,
  setFatalPlaybackError,
  setIsCallParentalPinLock,
  updateLiveProgramDetails,
  fatalPlaybackError,
  restartAssetId,
  programContent,
  isReadyToChangeStreams,
  hasChannelTuneInfo,
  setHasChannelTuneInfo,
  isDynatraceTrackingEnabled,
}) => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  useEffect(() => {
    const unrestrictedPlay =
      currentChannel?.metadata?.extendedMetadata?.isAllowUnrestrictedPlay === "Y" &&
      currentChannel?.metadata?.extendedMetadata?.isFast === "Y";
    if (isLivePlayer && useRoutePlaybackState.current && playbackAuthToken) {
      removeLocalStorage(VIDEO_AUTH_TOKEN);
      setLocalStorage(VIDEO_AUTH_TOKEN, playbackAuthToken, 90000);
    }
    if (
      (appProvider &&
        isLivePlayer &&
        currentChannel?.assets &&
        !isPPV &&
        isReadyToChangeStreams &&
        !useRoutePlaybackState.current) ||
      unrestrictedPlay
    ) {
      exitLookbackMode.current = false;
      const masterAsset = currentChannel.assets.find((asset) => asset.assetType?.toLowerCase() === ASSET_TYPES.MASTER);
      if (masterAsset) {
        setAssetId(masterAsset.assetId);
        !restartLiveStream &&
          (isCTTEnabled || unrestrictedPlay) &&
          !isLookbackStream &&
          setVideoUrl(masterAsset.videoUrl);
        setHasChannelTuneInfo(true);
        setLivePlaybackCanStart(false);
        dispatch(showPlaybackError(null, null));
        isCTTEnabled &&
          !restartLiveStream &&
          !isLookbackStream &&
          fetchVideoToken(appProvider, contentType, currentChannel.id, cancelTokenSource)
            .then((res) => {
              if (res.token) setAuthToken(res.token);
              removeLocalStorage(VIDEO_AUTH_TOKEN);
              setLocalStorage(VIDEO_AUTH_TOKEN, res.token, 90000);
            })
            .catch((e) => {
              console.error("Failed to get auth token, error = ", e);
            });
      } else {
        console.error("Live channel is missing master asset. Could not fetch stream info");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLivePlayer,
    appProvider,
    isPPV,
    currentChannel,
    contentType,
    dispatch,
    restartLiveStream,
    isReadyToChangeStreams,
    isCTTEnabled,
    isLookbackStream,
    playbackAuthToken,
  ]);

  useEffect(() => {
    if (!isLivePlayer || (isLivePlayer && isPPV) || !hasChannelTuneInfo) return;
    exitLookbackMode.current = false;
    const streamContentType = restartLiveStream || isLookbackStream ? PAGE_CONTENT_ITEM_TYPES.program : contentType;
    const streamAssetId = restartLiveStream || isLookbackStream ? restartAssetId.current : assetId;

    /** function to restart stream if the init start stream call failed  */
    const refetchStream = (assetId, parentalPin, tokenOnly = false) => {
      const streamAssetId = restartLiveStream || isLookbackStream ? restartAssetId.current : assetId;
      const promise = fetchVideoUrl(
        appProvider,
        streamContentType,
        currentChannelId,
        streamAssetId,
        parentalPin,
        tokenOnly,
        cancelTokenSource.current
      )
        .then((res) => {
          handleFetchStreamResponse({
            res,
            tokenOnly,
            parentalPin,
            pcPin,
            pcPinTimer,
            restartPcPinTimer,
            getMediaNodeMetaData,
            isConvivaAppTrackerEnabled,
            userProfile,
            appProvider,
            isInHome,
            isCTTEnabled,
            restartLiveStream,
            isLookbackStream,
            setVideoUrl,
            setAuthToken,
            removeLocalStorage,
            setLocalStorage,
            VIDEO_AUTH_TOKEN,
            setLivePlaybackCanStart,
            showPlaybackError,
            dispatch,
            isDynatraceTrackingEnabled,
          });
        })
        .catch((err) => {
          handleFetchStreamError({
            err,
            parentalPin,
            appProvider,
            telusConvivaAnalytics,
            translate,
            history,
            showModalPopup,
            dispatch,
            setFatalPlaybackError,
            showPlaybackError,
          });
        });
      return promise;
    };
    if (isReadyToChangeStreams && !fatalPlaybackError && !useRoutePlaybackState.current) {
      if (restartLiveStream) setVideoUrl(""); // // TSQA-43868 Biggest hack to address Restart -> Live issue
      fetchVideoUrl(
        appProvider,
        streamContentType,
        currentChannelId,
        streamAssetId,
        null,
        false,
        cancelTokenSource.current
      )
        .then((res) => {
          if (!isCTTEnabled || restartLiveStream || isLookbackStream) {
            if (res?.src) setVideoUrl(res.src);
            if (res?.token) {
              setAuthToken(res.token);
              removeLocalStorage(VIDEO_AUTH_TOKEN);
              setLocalStorage(VIDEO_AUTH_TOKEN, res.token, 90000);
            }
          }
          if (res?.src || res?.token) setLivePlaybackCanStart(true); // stream can start, live playback start signal to pass to player component
        })
        .catch((err) => {
          if (
            err?.code?.includes(AVS_ERROR_CODES.USER_NO_RIGHTS) ||
            AVS_ERROR_CODES.INCORRECT_PIN_CODES.includes(err?.code)
          ) {
            // user right and pin validation error code
            // client calls GET USERDATA/LIVE to determine reason of failure
            handleEntitlementError({
              err,
              appProvider,
              currentChannelId,
              contentType,
              cancelTokenSource,
              restartAssetId,
              pcPin,
              refetchStream,
              telusConvivaAnalytics,
              translate,
              history,
              showModalPopup,
              dispatch,
              setIsCallParentalPinLock,
              getMediaNodeMetaData,
              isDynatraceTrackingEnabled,
            });
          } else if (
            err?.code === AVS_ERROR_CODES.PANIC_MODE ||
            err?.code === AVS_ERROR_CODES.PC_LEVEL_BLOCKED_DURING_ECE
          ) {
            // handle panic mode AVS KO: 503-10000 or PC level blocked during ECE: 403-10147
            const modalContent = {
              title: translate("programming_unavailable"),
              history,
              isCloseable: true,
              message: translate("programming_unavailable_body")?.replace("%s", appProvider.pcLevelLabel),
            };
            dispatch(showModalPopup(MODAL_TYPES.ERROR, modalContent));
          } else if (
            ((err?.status && err.status !== 200) ||
              (err?.code && AVS_ERROR_CODES.SERVICE_OUTAGE_CODES.includes(err.code))) &&
            isCTTEnabled
          ) {
            // any Http error/Socket timeout or service outage error code ["500-10000", "500-10022", "500-10015"], we assume user is entitled for content, allows live playback
            setLivePlaybackCanStart(true);
          } else {
            // Any other error codes: stop playback and show error
            console.error(err);
            const AVSError = getAVSPlaybackError(err);
            setFatalPlaybackError(AVSError);
            dispatch(showPlaybackError(AVSError.code, AVSError.message));
            setLivePlaybackCanStart(true); // here we still need to toggle the livePlaybackCanStart state to render the live player. Because the playbackError has been set, the player will not start the playback but display the error message instead. See src/components/VideoPlayer for details
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appProvider,
    assetId,
    contentType,
    currentChannelId,
    getMediaNodeMetaData,
    hasChannelTuneInfo,
    history,
    isLivePlayer,
    dispatch,
    translate,
    restartLiveStream,
    isReadyToChangeStreams,
    fatalPlaybackError,
    isCTTEnabled,
    isLookbackStream,
    isPPV,
    isInHome,
    programContent,
    userProfile,
    isConvivaAppTrackerEnabled,
    isDynatraceTrackingEnabled,
  ]);

  useEffect(() => {
    if (isReadyToChangeStreams && !fatalPlaybackError) {
      if (isLivePlayer) updateLiveProgramDetails();
    }
  }, [fatalPlaybackError, isLivePlayer, isReadyToChangeStreams, updateLiveProgramDetails]);
};
